import * as _ from 'lodash';
import { connectionPointsWithFrequency, importConnectionPointTypes, connectionPointTypes } from '../../common/constants';

import {
    ConnectionPoint,
    TagDefinition,
    MailingJobDefinitionDto,
    AlarmEmailDTO,
    HistorianStorageConfiguration,
    ConnectionPointViewModel,
    ConnectionPointStatusViewModel
} from '../../models';
import { ConnectionPointType } from '../../models/enum';
import { IFrequencyFormatHelperService } from '../../services/helpers';

export interface IConnectionPointTuple {
    model: ConnectionPoint;
    viewModel: ConnectionPointViewModel;
    isAvailable?: boolean;
    isUpToDate?: boolean;
}

export interface IConnectionPointMappingService {
    computeViewModels(
        connectionPoints: ConnectionPoint[],
        defaultDbGuid: string,
        tags: TagDefinition[],
        emails: MailingJobDefinitionDto[],
        alarms: AlarmEmailDTO[],
        storages: HistorianStorageConfiguration[]
    ): IConnectionPointTuple[];
}

export class ConnectionPointMappingService implements IConnectionPointMappingService {
    static serviceId: string = 'connectionPointMappingService';
    static $inject = ['frequencyFormatHelperService'];

    private _hasFrequency = connectionPointsWithFrequency;
    private _importTypes = importConnectionPointTypes;

    constructor(private _frequencyFormatHelperService: IFrequencyFormatHelperService) {}

    computeViewModels(
        connectionPoints: ConnectionPoint[],
        defaultDbGuid: string,
        tags: TagDefinition[],
        emails: MailingJobDefinitionDto[],
        alarms: AlarmEmailDTO[],
        storages: HistorianStorageConfiguration[]
    ): IConnectionPointTuple[] {
        return connectionPoints.map((cp) => this.map(cp, defaultDbGuid, tags, emails, alarms, storages, connectionPoints));
    }

    private map(
        cp: ConnectionPoint,
        defaultDbGuid: string,
        tags: TagDefinition[],
        emails: MailingJobDefinitionDto[],
        alarms: AlarmEmailDTO[],
        storages: HistorianStorageConfiguration[],
        connectionPoints: ConnectionPoint[]
    ): IConnectionPointTuple {
        try {
            let isImportType = this._importTypes.indexOf(cp.type) > -1;
            if (isImportType) {
                isImportType = cp.type !== ConnectionPointType.PIAF || (cp.type === ConnectionPointType.PIAF && !cp.instance);
            }
            isImportType = isImportType || !!cp.tagDefinitionFile;

            let status: ConnectionPointStatusViewModel = {
                iconClass: 'fa-check success',
                panel: {
                    class: null,
                    icon: null
                },
                message: null
            };

            if (cp.type === ConnectionPointType.PI) {
                status = {
                    iconClass: 'fa-exclamation-triangle warning',
                    panel: { class: 'panel-warning', icon: 'fa-exclamation-triangle' },
                    message: 'PI SDK will be deprecated in the future. Consider using PI AF instead'
                };
            }

            if (cp.type === ConnectionPointType.Excel) {
                status = {
                    iconClass: 'fa-exclamation-triangle warning',
                    panel: { class: 'panel-warning', icon: 'fa-exclamation-triangle' },
                    message: 'Excel is deprecated.'
                };
            }

            const usages = this.getUsages(cp, defaultDbGuid, tags, emails, alarms, storages, connectionPoints);
            const res: { model: ConnectionPoint; viewModel: ConnectionPointViewModel } = {
                model: cp,
                viewModel: {
                    address: cp.address,
                    allowOverwrite: cp.allowOverwrite,
                    alternateServerGuid: cp.alternateServerGuid,
                    bulkInsertEnabled: cp.bulkInsertEnabled,
                    canDelete: usages.length === 0,
                    canImportGroups: cp.type === ConnectionPointType.PIAF && !!cp.instance,
                    canImportTags: isImportType,
                    connectionPointType: _.find(connectionPointTypes, (c) => c.key === cp.type).value,
                    creationDate: cp.creationDate,
                    creationUser: cp.creationUser,
                    dateAlignment: cp.dateAlignment,
                    documentation: cp.documentation,
                    frequency: cp.interval && this._hasFrequency.indexOf(cp.type) >= 0 ? this.getFrequencyLabel(cp) : '-',
                    guid: cp.guid,
                    ignoreErrors: cp.ignoreErrors,
                    ignoreSSLError: cp.ignoreSSLError,
                    instance: cp.instance,
                    interval: cp.interval,
                    intervalTimeUnit: cp.intervalTimeUnit,
                    isAvailable: true,
                    isDefault: cp.guid === defaultDbGuid,
                    isExportingLogbook: cp.isExportingLogbook,
                    isSmtp: cp.type === ConnectionPointType.SMTP,
                    isSmtpSending: false,
                    isUpToDate: true,
                    isValid: this.checkNeedConfiguration(cp),
                    keyvault: cp.keyvault,
                    lastModificationDate: cp.lastModificationDate,
                    lastModifier: cp.lastModifier,
                    longComment: cp.longComment,
                    name: cp.name,
                    numberOfTags: tags.filter((t) => t.acquisitionPointGuid === cp.guid).length,
                    password: '',
                    shortComment: cp.shortComment,
                    status,
                    testResult: false,
                    type: cp.type,
                    usages: usages,
                    userName: ''
                }
            };
            return res;
        } catch (e) {
            console.error(e);
            return null;
        }
    }

    private getUsages(
        cp: ConnectionPoint,
        defaultDbGuid: string,
        tags: TagDefinition[],
        emails: MailingJobDefinitionDto[],
        alarms: AlarmEmailDTO[],
        storages: HistorianStorageConfiguration[],
        connectionPoints: ConnectionPoint[]
    ) {
        const result = [];

        if (cp.guid === defaultDbGuid) {
            result.push({ type: 'default', guid: [cp.guid] });
        }

        const matchingTags = tags.filter((t) => t.acquisitionPointGuid === cp.guid);
        if (matchingTags.length > 0) {
            result.push({ type: 'tag', guid: matchingTags.map((t) => t.guid) });
        }

        const matchingStorages = storages.filter((s) => s.connectionPointGuid === cp.guid);
        if (matchingStorages.length > 0) {
            result.push({ type: 'storage', guid: matchingTags.map((t) => t.guid) });
        }

        if (cp.type === ConnectionPointType.SMTP) {
            const matchingEmails = emails.filter((s) => s.connectionPointGuid === cp.guid);
            if (matchingEmails.length > 0) {
                result.push({ type: 'email', guid: matchingEmails.map((t) => t.guid) });
            }

            const matchingAlarms = alarms.filter((s) => s.eventEmailDefinition.connectionPointGuid === cp.guid);
            if (matchingAlarms.length > 0) {
                result.push({ type: 'alarm', guid: matchingAlarms.map((t) => t.guid) });
            }
        }

        const matchingAlternates = connectionPoints.filter((c) => c.alternateServerGuid === cp.guid);
        if (matchingAlternates.length > 0) {
            result.push({ type: 'connectionPoint', guid: matchingAlternates.map((d) => d.guid) });
        }

        return result;
    }

    private getFrequencyLabel(cp: ConnectionPoint): string {
        return this._frequencyFormatHelperService.formatFrequency(cp);
    }

    private checkNeedConfiguration(connectionPoint: ConnectionPoint): boolean {
        let result = false;

        //Following todo was copied and pasted
        // TODO
        // this validity test should be narrowed with using the connection point type, using  :
        // private _hasAddress = [connectionPointType.PI, connectionPointType.Excel, connectionPointType.SQL, connectionPointType.WideDatabase, connectionPointType.csv, connectionPointType.OPCHDA];
        // private _hasInstance = [connectionPointType.SQL, connectionPointType.WideDatabase, connectionPointType.OPCHDA];
        // private _hasCredentials = [connectionPointType.PI, connectionPointType.SQL, connectionPointType.WideDatabase];
        // private _hasFrequency = [connectionPointType.WideDatabase];
        // WICH should be placed in service

        switch (connectionPoint.type) {
            case ConnectionPointType.PI:
            case ConnectionPointType.PIAF:
            case ConnectionPointType.OPCHDA:
            case ConnectionPointType.CSVDataTable:
            case ConnectionPointType.Oracle:
            case ConnectionPointType.SQL:
            case ConnectionPointType.GridVis:
            case ConnectionPointType.Npgsql:
                result = !!connectionPoint.address;
                break;
            case ConnectionPointType.WideDatabase:
                result = !!connectionPoint.address && !!connectionPoint.instance;
                // && !!connectionPoint.userName && !!connectionPoint.password
                break;
            case ConnectionPointType.Manual:
            case ConnectionPointType.DataGeneration:
                result = true;
                break;
            default:
                result = true;
                break;
        }

        return result;
    }
}
